import React, { useContext, useEffect, useState, useMemo } from "react";
import { Users } from "lucide-react";
import { apiFetch } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import ShareButton from "../../components/sharebtn.js";
import WelcomeCard from "../../components/WelcomeCard";
import StatsGrid from "../../components/StatsGrid";
import * as icons from "../../constant/icons";
// Import theme context for dark/light mode
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";

// Local modules
import { API_URL } from "./config";
import { text } from "./locales";
import { useResponsive } from "./hooks/useResponsive";
import { useEmployees } from "./hooks/useEmployees";
import { useEmployeeStats } from "./hooks/useEmployeeStats";
import { useArchivedEmployees } from "./hooks/useArchivedEmployees";
import { useCompanies } from "./hooks/useCompanies";
import { useEmployeeProfilePics } from "./hooks/useEmployeeProfilePics";
import { useDocumentChecklist } from "./hooks/useDocumentChecklist";
import { useEmployeeActions } from "./hooks/useEmployeeActions";
import { useExportEmployees } from "./hooks/useExportEmployees";
import {
  getResponsiveStyles,
  createHoverHandlers,
} from "./styles/employeeManagementStyles";
import Alert from "./components/common/Alert";
import EmployeeFilters from "./components/EmployeeFilters";
import EmployeeTable from "./components/EmployeeTable";
import StatCardsGrid from "./components/StatCardsGrid";
import AddEmployeeModal from "./components/modals/AddEmployeeModal";
import EditEmployeeModal from "./components/modals/EditEmployeeModal";
import ViewEmployeeModal from "./components/modals/ViewEmployeeModal";
import ArchiveModal from "./components/modals/ArchiveModal";
import DocumentsModal from "./components/modals/DocumentsModal";

// Main Employee Management component
function EmployeeManagementManager({ onCountsUpdate, onEmployeeCountUpdate } = {}) {
  // Get theme context for styling
  const themeContext = useContext(ThemeContext);
  const languageContext = useContext(LanguageContext);

  const theme = themeContext?.theme || 'light';
  const language = languageContext?.language || 'en';
  const { profile } = useProfile();

  const isDarkMode = theme === "dark";

  // Debug log
  console.log('EmployeeManagementManager rendered', { theme, language });

  // ===== RESPONSIVE STATE MANAGEMENT =====
  const { screenSize, spacing, fonts, gridCols } = useResponsive();

  // ===== EXISTING STATE MANAGEMENT =====
  // Modal visibility states
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [filterCompanySearch, setFilterCompanySearch] = useState("");

  // Selected employee for view/edit operations
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterStatusSearch, setFilterStatusSearch] = useState("");

  // Loading and message states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ===== DATA HOOKS =====
  const {
    employees,
    setEmployees,
    toggleEmployeeExpand,
    fetchEmployees,
  } = useEmployees({ profile, language, text, setLoading, setError });

  const { employeeCount, counts, fetchEmployeeCount, fetchEmployeeCounts } =
    useEmployeeStats({ profile, onCountsUpdate, onEmployeeCountUpdate, setError });

  const {
    archivedEmployees,
    setArchivedEmployees,
    archivedCount,
    setArchivedCount,
    fetchArchivedEmployees,
  } = useArchivedEmployees({ profile, language });

  const { companies, fetchCompanies } = useCompanies({ profile });

  const { employeeProfilePics } = useEmployeeProfilePics({ employees });

  const {
    showDocumentsModal,
    setShowDocumentsModal,
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,
    setHasUnsavedChanges,
    savingDocument,
    hasUnsavedChanges,
    documentChecklist,
    setDocumentChecklist,
    handleViewDocuments,
    handleDocumentCheckboxChange,
    handleSaveDocuments,
  } = useDocumentChecklist({ language, text, setEmployees, setError });

  // Effect to fetch employee count on component mount
  useEffect(() => {
    fetchEmployeeCount();
    fetchArchivedEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to fetch employee counts by status
  useEffect(() => {
    fetchEmployeeCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to fetch all employees
  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to handle viewing employee details
  const handleViewEmployee = (employee) => {
    // Set selected employee
    setSelectedEmployee(employee);
    // Initialize document checklist from employee data
    const empData = employee.fullEmployeeData || {};
    setDocumentChecklist({
      photosCount: empData.photosCount || false,
      birthCertificateCopy: empData.birthCertificateCopy || false,
      gradCertificateOriginal: empData.gradCertificateOriginal || false,
      idCopy: empData.idCopy || false,
      noCrimeCertificate: empData.noCrimeCertificate || false,
      laborLetterOriginal: empData.laborLetterOriginal || false,
      medicalCheckForm111: empData.medicalCheckForm111 || false,
    });
    // Show view modal
    setShowViewEmployee(true);
  };

  // Function to handle editing employee
  // eslint-disable-next-line no-unused-vars
  const handleEditEmployee = (employee) => {
    // Set selected employee
    setSelectedEmployee(employee);
    // Show edit modal
    setShowEditEmployee(true);
  };

  // ===== CRUD ACTIONS =====
  const {
    handleUpdateEmployee,
    handleAddEmployee,
    handleRestoreEmployee,
  } = useEmployeeActions({
    language,
    text,
    employees,
    setEmployees,
    archivedEmployees,
    setArchivedEmployees,
    archivedCount,
    setArchivedCount,
    selectedEmployee,
    setSelectedEmployee,
    setShowAddEmployee,
    setShowEditEmployee,
    setLoading,
    setError,
    setSuccess,
    fetchEmployeeCount,
    fetchEmployeeCounts,
  });

  // Filter employees based on search and filter criteria
  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      (employee.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (employee.id?.toString() || "").includes(searchTerm.toLowerCase()) ||
      (employee.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    const matchesDepartment =
      filterDepartment === "" ||
      (employee.department?.toLowerCase() || "").includes(filterDepartment.toLowerCase());

    const matchesStatus =
      filterStatus === "" || employee.status === filterStatus;

    // ✅ FIXED: Use workingCompany from fullEmployeeData
    const matchesCompany =
      filterCompany === "" ||
      employee.fullEmployeeData?.workingCompany === filterCompany;

    return (
      matchesSearch && matchesDepartment && matchesStatus && matchesCompany
    );
  });

  // Calculate actual visible stats instead of backend global counts
  const calculatedStats = useMemo(() => {
    let active = 0;
    let onLeave = 0;
    employees.forEach((emp) => {
      const status = (emp.status || emp.employeeStatus || "").toLowerCase().replace(/\s+/g, "");
      if (status === "active") active++;
      else if (status === "onleave") onLeave++;
    });
    return { Active: active, OnLeave: onLeave, Total: employees.length };
  }, [employees]);

  // ===== EXPORT / SHARE =====
  const { handleExportToExcel, handleShareReport } = useExportEmployees({
    filteredEmployees,
    searchTerm,
    filterDepartment,
    filterStatus,
    filterCompany,
    language,
    text,
    setLoading,
    setError,
    setSuccess,
  });

  // Get styles object
  const styles = getResponsiveStyles({ theme, screenSize, spacing, fonts, language });

  // ===== EVENT HANDLERS FOR HOVER EFFECTS =====
  const {
    handleSecondaryBtnMouseEnter,
    handleSecondaryBtnMouseLeave,
  } = createHoverHandlers({ theme, screenSize, loading });

  // ===== MAIN COMPONENT RETURN =====
  try {
    return (
      <div style={{ ...styles.container, display: "flex", flexDirection: "column", gap: "8px" }}>
        {/* ===== RESPONSIVE GLOBAL STYLES ===== */}
        <style>
          {`
          /* ===== RESPONSIVE CSS ANIMATIONS ===== */
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(${screenSize === "xs" ? "0.5rem" : "1rem"});
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(${screenSize === "xs" ? "1.05" : "1.15"});
            opacity: 0.8;
          }
        }
          select option {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#1715247e"} !important;
    color: #fff !important;
    padding: 12px;

  }

  select option:hover {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#3a39457e"} !important;
  }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(${screenSize === "xs" ? "0.5rem" : "0.625rem"
            });
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ===== RESPONSIVE SCROLLBAR STYLES ===== */
        ::-webkit-scrollbar {
          width: ${screenSize === "xs"
              ? "0.25rem"
              : screenSize === "sm"
                ? "0.375rem"
                : "0.5rem"
            };
          height: ${screenSize === "xs"
              ? "0.25rem"
              : screenSize === "sm"
                ? "0.375rem"
                : "0.5rem"
            };
        }

        ::-webkit-scrollbar-track {
          background: ${theme === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)"
            };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
            };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
            };
        }
          .modal::-webkit-scrollbar {
  display: none;
}


        /* ===== RESPONSIVE BODY STYLES ===== */
        body {
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
        }

        /* ===== RESPONSIVE UTILITY CLASSES ===== */
        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .pulse-animation {
          animation: pulse 2s infinite;
        }

        /* ===== RESPONSIVE FOCUS STYLES ===== */
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 ${screenSize === "xs" ? "0.125rem" : "0.25rem"
            } rgba(59, 130, 246, 0.15) !important;
        }

        /* ===== RESPONSIVE TABLE STYLES ===== */
        table {
          border-collapse: separate;
          border-spacing: 0;
        }

        /* ===== RESPONSIVE BUTTON STYLES ===== */
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ===== RESPONSIVE MEDIA QUERIES ===== */
        @media (max-width: 480px) {
          .hide-on-mobile {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .hide-on-tablet {
            display: none !important;
          }
        }

        @media (max-width: 1024px) {
          .hide-on-desktop {
            display: none !important;
          }
        }
        `}
        </style>

        {/* Error Alert */}
        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError("")}
            styles={styles}
            screenSize={screenSize}
            fonts={fonts}
          />
        )}

        {/* Success Alert */}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess("")}
            styles={styles}
            screenSize={screenSize}
            fonts={fonts}
          />
        )}

        {/* Welcome Section */}
        <div style={{ marginBottom: "-20px" }}>
          <WelcomeCard
            styles={styles}
            text={text}
            language={language}
            userName={
              language === "ar"
                ? localStorage.getItem("employeeArabicName")?.split(" ")[0] || ""
                : localStorage.getItem("employeeName")?.split(" ")[0] || ""
            }
            title={{ en: text.en.title, ar: text.ar.title }}
            subtitle={language === "ar" ? "إدارة وتنظيم ومتابعة الموظفين" : "Manage, organize, and track your workforce efficiently."}
          />
        </div>

        {/* Responsive Statistics Cards Grid */}
        <StatsGrid
          stats={[
            {
              title: text[language].totalEmployees || "Total Employees",
              value: calculatedStats.Total,
              icon: icons.Users,
              iconBackground: "rgb(0, 180, 72)",
              showSymbol: true,
              valueAlign: 50,
              subtitle: language === "ar" ? "كل الموظفين" : "All Employees",
            },
            {
              title: text[language].active || "Active",
              value: calculatedStats.Active,
              description: text[language].thisWeek || "This week",
              icon: icons.UserCheck,
              iconBackground: "rgb(6, 0, 180)",
              valueAlign: 50,
              subtitle: language === "ar" ? "هذا الأسبوع" : "This week",
            },
            {
              title: text[language].onLeave || "On Leave",
              value: calculatedStats.OnLeave,
              description: text[language].today || "Today",
              icon: icons.Calendar,
              iconBackground: "rgb(180, 0, 72)",
              valueAlign: 50,
              subtitle: language === "ar" ? "اليوم" : "Today",
            },
            {
              title: text[language].archived || "Archived",
              value: archivedCount,
              description: text[language].ovearall || "Overall",
              icon: icons.Building,
              iconBackground: "rgb(96, 96, 96)",
              valueAlign: 50,
              subtitle: language === "ar" ? "بشكل عام" : "Overall",
            },
          ]}
          styles={styles}
          theme={theme}
          screenSize={screenSize}
        />

        {/* Filters and Actions Section */}
        <div style={styles.statCard}>

          {/* Filter Controls */}
          <EmployeeFilters
            styles={styles}
            text={text}
            language={language}
            theme={theme}
            screenSize={screenSize}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterDepartment={filterDepartment}
            setFilterDepartment={setFilterDepartment}
            filterStatusSearch={filterStatusSearch}
            setFilterStatusSearch={setFilterStatusSearch}
            setFilterStatus={setFilterStatus}
            filterCompanySearch={filterCompanySearch}
            setFilterCompanySearch={setFilterCompanySearch}
            setFilterCompany={setFilterCompany}
            companies={companies}
          />

          {/* Action Buttons Section (Placed under the filters) */}
          <div style={{ marginBottom: spacing.margin }}>
            <div style={styles.topButtonsContainer}>
              {/* Export/Share Button */}
              <ShareButton
                handleExportToExcel={handleExportToExcel}
                handleShareReport={handleShareReport}
                loading={loading}
                styles={styles}
                handleSecondaryBtnMouseEnter={handleSecondaryBtnMouseEnter}
                handleSecondaryBtnMouseLeave={handleSecondaryBtnMouseLeave}
              />
            </div>
          </div>
        </div>

        {/* Responsive Table with Headers (Hierarchy supported) */}
        <div style={styles.statCard}>
          <h3 style={styles.welcomeTitle}>
            <Users size={screenSize === "xs" ? 18 : 20} />
            {screenSize !== "xs" &&
              `${text[language].employeeList} (${filteredEmployees.length} ${text[language].employeesCount})`}
            {screenSize === "xs" &&
              `${text[language].employees} (${filteredEmployees.length})`}
            {loading && (
              <span style={{ fontSize: fonts.body, opacity: 0.7 }}>
                {" "}
                - {text[language].loading}
              </span>
            )}
          </h3>

          <EmployeeTable
            styles={styles}
            text={text}
            language={language}
            theme={theme}
            screenSize={screenSize}
            fonts={fonts}
            spacing={spacing}
            filteredEmployees={filteredEmployees}
            employeeProfilePics={employeeProfilePics}
            toggleEmployeeExpand={toggleEmployeeExpand}
            onViewEmployee={handleViewEmployee}
            onViewDocuments={handleViewDocuments}
          />
        </div>


        {/* Render Modals */}
        {showAddEmployee && (
          <AddEmployeeModal
            styles={styles}
            text={text}
            language={language}
            theme={theme}
            screenSize={screenSize}
            onAdd={handleAddEmployee}
            onClose={() => setShowAddEmployee(false)}
          />
        )}
        {showViewEmployee && (
          <ViewEmployeeModal
            styles={styles}
            text={text}
            language={language}
            screenSize={screenSize}
            fonts={fonts}
            spacing={spacing}
            isDarkMode={isDarkMode}
            selectedEmployee={selectedEmployee}
            employeeProfilePics={employeeProfilePics}
            onClose={() => setShowViewEmployee(false)}
            documentChecklist={documentChecklist}
            hasUnsavedChanges={hasUnsavedChanges}
            savingDocument={savingDocument}
            onCheckboxChange={handleDocumentCheckboxChange}
            onSaveDocuments={handleSaveDocuments}
          />
        )}
        {showEditEmployee && (
          <EditEmployeeModal
            styles={styles}
            text={text}
            language={language}
            theme={theme}
            screenSize={screenSize}
            loading={loading}
            selectedEmployee={selectedEmployee}
            onUpdate={handleUpdateEmployee}
            onCancel={() => {
              setShowEditEmployee(false);
              setSelectedEmployee(null);
            }}
            onBackdropClick={() => setShowAddEmployee(false)}
          />
        )}
        {showArchive && (
          <ArchiveModal
            styles={styles}
            text={text}
            language={language}
            theme={theme}
            screenSize={screenSize}
            fonts={fonts}
            spacing={spacing}
            archivedEmployees={archivedEmployees}
            archivedCount={archivedCount}
            employeeProfilePics={employeeProfilePics}
            onClose={() => setShowArchive(false)}
            onRestore={handleRestoreEmployee}
            onView={handleViewEmployee}
            onRestoreAll={() => {
              if (
                window.confirm(
                  `${text[language].confirmRestoreAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion}`
                )
              ) {
                // Restore all archived employees
                archivedEmployees.forEach((emp) =>
                  handleRestoreEmployee(emp.id)
                );
              }
            }}
            onDeleteAll={() => {
              if (
                window.confirm(
                  `${text[language].confirmDeleteAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion} ${text[language].cannotBeUndone}`
                )
              ) {
                // Clear all archived employees
                setArchivedEmployees([]);
                setArchivedCount(0);
                setSuccess(text[language].allEmployeesDeleted);

                // Optional: Make API call to clear archived employees
                apiFetch(`${API_URL}/employeesa/archived`, { method: "DELETE" });
              }
            }}
          />
        )}
        {showDocumentsModal && (
          <DocumentsModal
            styles={styles}
            text={text}
            language={language}
            screenSize={screenSize}
            fonts={fonts}
            spacing={spacing}
            isDarkMode={isDarkMode}
            selectedEmployeeDocuments={selectedEmployeeDocuments}
            documentChecklist={documentChecklist}
            hasUnsavedChanges={hasUnsavedChanges}
            savingDocument={savingDocument}
            onCheckboxChange={handleDocumentCheckboxChange}
            onSave={handleSaveDocuments}
            onBackdropClose={() => {
              setShowDocumentsModal(false);
              setSelectedEmployeeDocuments(null);
            }}
            onClose={() => {
              setShowDocumentsModal(false);
              setSelectedEmployeeDocuments(null);
              setHasUnsavedChanges(false);
            }}
          />
        )}
      </div>
    );
  } catch (error) {
    console.error('Error rendering EmployeeManagementManager:', error);
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#ef4444'
      }}>
        <h1>Error Loading Employee Management</h1>
        <p>{error.message}</p>
        <p>Please check the console for more details.</p>
      </div>
    );
  }
}

// Export the component using export default syntax
export default EmployeeManagementManager;
